import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { UserResponseDTO, PermissionResponseDTO } from "@/shared/dto";

interface User extends UserResponseDTO { }

interface Permission extends PermissionResponseDTO { }

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  user: User | null;
  permissions: Permission[];
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (
    accessToken: string,
    refreshToken: string,
    user: User,
    permissions: Permission[]
  ) => void;
  logout: () => void;
  setLoading: (loading: boolean) => void;
  updateTokens: (refreshToken: string, accessToken: string) => void;
  updateUser: (user: User) => void;
  updatePermissions: (permissions: Permission[]) => void;
  hasPermission: (guardSlug: string) => boolean;
  hasAnyPermission: (guardSlugs: string[]) => boolean;
  hasAllPermissions: (guardSlugs: string[]) => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      refreshToken: null,
      user: null,
      permissions: [],
      isAuthenticated: false,
      isLoading: false,

      login: (accessToken, refreshToken, user, permissions) =>
        set({
          accessToken,
          refreshToken,
          user,
          permissions,
          isAuthenticated: true,
        }),

      logout: () =>
        set({
          accessToken: null,
          refreshToken: null,
          user: null,
          permissions: [],
          isAuthenticated: false,
        }),

      setLoading: (loading) => set({ isLoading: loading }),

      updateTokens: (refreshToken: string, accessToken: string) =>
        set({ refreshToken, accessToken }),

      updateUser: (user) => set({ user }),

      updatePermissions: (permissions) => set({ permissions }),

      hasPermission: (guardSlug: string) => {
        const { permissions } = get();
        return permissions.some((perm) => perm.guardSlug === guardSlug);
      },

      hasAnyPermission: (guardSlugs: string[]) => {
        const { permissions } = get();
        return guardSlugs.some((slug) =>
          permissions.some((perm) => perm.guardSlug === slug)
        );
      },

      hasAllPermissions: (guardSlugs: string[]) => {
        const { permissions } = get();
        return guardSlugs.every((slug) =>
          permissions.some((perm) => perm.guardSlug === slug)
        );
      },
    }),
    {
      name: "auth-storage",
    }
  )
);
